import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import parlors from "@/constants/parlors_detail.json";

import NotFoundPage from "pages/404";
import { FUNERAL_SIGUNGU } from "goi_common";
import { getThreeClosestParlorFrom } from "@/containers/Parlor/ParlorDetailContainer/utils/getThreeClosestParlorFrom";
import dayjs from "dayjs";
import { Box } from "@chakra-ui/react";
import ParlorRequestContainer from "@/containers/Parlor/ParlorRequestContainer";
import { ParlorDetailProps } from "@/types/parlor";

import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import foodPerPerson from "@/constants/parlor/parlorFoodPerPerson.json";
import { getEskyPrice } from "@/utils/eskyPrice";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = parlors.map((parlor) => ({
    params: {
      id: String(parlor.id),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let sigungu;
  let name = "";
  let description = "";
  let closestParlorsByService = {};

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      name = parlor.companyname;
      sigungu = FUNERAL_SIGUNGU.get(Number(parlor?.sido))?.get(Number(parlor?.sigungu));
      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      // let binsoString = "";
      // parlor.binsos.forEach((binso: BinsoObject) => {
      //   if (binso.pyeong) {
      //     binsoString += `${binso.pyeong.toFixed()}평형 ${numberToLocalString(binso.charge)}원, `;
      //   }
      // });

      // description =
      //   name +
      //   " 정보 안내," +
      //   " 빈소 평형별 정보 (1일/24시간 기준) - 고이장례연구소 예상 비용(자체 산정)\n" +
      //   binsoString.slice(0, binsoString.length - 2) +
      //   "\n" +
      //   `, 주소:${parlor.address}, 전화번호:${parlor.telephone}. 빈소 평형별 정보 안내`;

      description =
        `${name}(${sigungu})` +
        // " 장례, " +
        // `${name}(${sigungu})` +
        // " 장례식 예상 비용" +
        // `${name}(${sigungu})` +
        // " 3일장 장례 비용, " +
        // `${name}(${sigungu})` +
        // " 평균 장례 비용, " +
        // `${name}(${sigungu})` +
        // " 빈소 유형\n" +
        // binsoString.slice(0, binsoString.length - 2) +
        // "\n" +
        // `${name}(${sigungu})` +
        " - 고이장례연구소 예상 장례 비용(자체 산정) " +
        parlor.expected_charge_sum["무빈소"].toLocaleString() +
        "원 ~ " +
        parlor.expected_charge_sum["일반장"].toLocaleString() +
        "원 " +
        "\n" +
        `장례식장별 비교, 추천, 예상 비용 안내. ${name}(${sigungu}) 포함`;

      closestParlorsByService = {
        무빈소: getThreeClosestParlorFrom(parlor as any, "무빈소"),
        가족장: getThreeClosestParlorFrom(parlor as any, "가족장"),
        일반장: getThreeClosestParlorFrom(parlor as any, "일반장"),
      };
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${name}(${sigungu}), ${name}(${sigungu}) 장례 비용, ${district} 장례 견적, ${name} 평균 장례 비용`,
      // pageOgTitle: `${name}(${sigungu}) 장례식 예상 비용 - [장례의 모든 것, 고이장례연구소]`,
      pageOgTitle: `${name}(${sigungu}) 포함 장례식 예상 비용, 장례식장별 비교, 추천 - [숨김 없는 장례의 시작, 고이]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      hasError: !parlor,
      // E5 조문객 질문 범위용 1인 1식 음식값(원). 없으면 null
      foodPerPersonWon:
        (parlor && (foodPerPerson.byId as Record<string, number[]>)[String(parlor.id)]?.[0]) ??
        null,
      // 제단꽃 공시가(e하늘). 30만 원 미만은 부분 품목(리본 등)이라 뺀다. 없으면 null
      // - altarWon: 기본(가장 싼 것). 조문객 질문 범위 · 비용 구성
      // - altarGradesWon: 기본 · 특 · VIP(가장 싼 것 · 가운데 · 위쪽 4분의 1). 직접 맞춰 보기용(지금은 꺼 둠)
      ...(() => {
        const altar = parlor ? getAltarPrices(parlor.id) : null;
        return {
          altarWon: altar?.min ?? null,
          altarGradesWon: altar?.grades ?? null,
        };
      })(),
      closestParlorsByService,
      // 네이버는 사이트당 웹문서 1개만 노출한다. 견적폼이 상세의 자리를 뺏지 않도록 상세로 몬다
      pageCanonical: parlor ? `https://www.goifuneral.co.kr/parlor/${parlor.id}/` : null,
    },
  };
};

const getAltarPrices = (id: number): { min: number | null; grades: number[] | null } => {
  const prices = (getEskyPrice(id)?.items ?? [])
    .filter((item) => item.group === "제단" && item.price >= 300_000)
    .map((item) => item.price)
    .sort((a, b) => a - b);
  if (prices.length === 0) return { min: null, grades: null };
  const mid = prices[Math.floor(prices.length / 2)];
  return {
    min: prices[0],
    // 고급은 가장 비싼 것이 아니라 위쪽 4분의 1 지점. 가장 비싼 건 특수 제단이라 너무 튀었다(아산 900만 원, 11-05 주완)
    grades: Array.from(new Set([prices[0], mid, prices[Math.floor(prices.length * 0.75)]])),
  };
};

function ParlorRequest({
  defaultParlor,
  hasError,
  foodPerPersonWon,
  altarWon,
  altarGradesWon,
}: {
  defaultParlor: ParlorDetailProps;
  hasError: boolean;
  foodPerPersonWon: number | null;
  altarWon: number | null;
  altarGradesWon: number[] | null;
}) {
  // const { howtoRichSearch, carouselRichSearch, reviewResult76 } = richSearchData.parlor;
  // const { faqList, reviewResult } = richSearchData.common;
  if (hasError) return <NotFoundPage />;
  const today = dayjs().format("YYYY-MM-DD");
  return (
    <>
      <Box position="absolute" marginTop="-90px">
        <time dateTime={today} className="white se_publishDate pcol2 date">
          {today}
        </time>
      </Box>

      {/* <RichSearch key="faqs" data={faqList} />
      <RichSearch key="howto" data={howtoRichSearch} />
      <RichSearch key="carousel" data={carouselRichSearch} /> */}
      {/* <RichSearch
        key="review"
        data={String(defaultParlor.id) === "76" ? reviewResult76 : reviewResult}
      /> */}
      <ParlorRequestContainer
        parlor={defaultParlor}
        foodPerPersonWon={foodPerPersonWon}
        altarWon={altarWon}
        altarGradesWon={altarGradesWon}
      />
    </>
  );
}

export default ParlorRequest;
